<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Clock In/Clock Out" class="px-4 pr-5 mb-5">
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-2 pb-3"
      >
        <v-col cols="12" sm="5" md="4" lg="3">
          <WeekPicker
            :date="dateFilter"
            :hideDetails="true"
            @weekChange="weekChangeHandler"
            @dateChange="(newValue) => this.dateFilter = newValue"
          ></WeekPicker>
        </v-col>
        <v-col cols="12" sm="7" md="5" lg="6" xl="4">
          <v-select
            label="Contract"
            v-model="selectedAssignmentId"
            :items="assignmentsOptionsList"
            item-text="text"
            item-value="value"
            outlined
            hide-details
            @change="selectedAssignmentChangeHandler"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3" xl="2" style="color: rgba(0,0,0,.6);" class="text-center" v-if="!showNoDataMessage">
          <v-chip
            color="primary"
            class="py-5"
          >
            Total:&nbsp;&nbsp;{{ parseFloat(weekTotalHours).toFixed(2) }} Hours
          </v-chip>
        </v-col>
      </v-row>
    </div>
    <v-container fluid class="px-0 pt-2">
      <v-card style="max-height: 590px; overflow-y: auto; overflow-x: hidden;" v-if="!showNoDataMessage">
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="4" lg="3" xl="2"
              style="min-height: 290px; max-height: 290px;"
              v-for="day in clockWeekLogs.clockDaysLog" :key="day.utcDate"
            >
              <ClockDayLog
                :localDateTime="day.staffLynxLocalDateTime"
                :clockLogs="day.clockLogs"
                :dayOfTheWeek="day.staffLynxLocalDayOfTheWeek"
                :workedTime="day.workedTime"
              ></ClockDayLog>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <div v-if="showNoDataMessage">
        <WithoutAssignmentEntriesView
        ></WithoutAssignmentEntriesView>
      </div>
    </v-container>
    <div class="hide-on-sm hide-on-print">
      <v-row justify="end" v-if="currentWeek && !showNoDataMessage">
        <v-col cols="6" md="3">
          <v-btn
            block
            depressed
            color="primary"
            @click="clockHandler"
          >
            {{ acceptLabel }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div class="hide-on-md hide-on-print mt-5 abs-left-0" v-if="currentWeek && !showNoDataMessage">
      <v-container fluid class="fixed-buttons mt-0 px-0">
        <v-layout row>
          <v-flex xs12 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              color="primary"
              @click="clockHandler"
            >
              {{ acceptLabel }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
    <MessageBox
      ref="messageBox"
      :message="messageBoxText"
      :color="messageBoxColor"
      closeTextColor="white"
    ></MessageBox>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import ClockDayLog from './ClockDayLog.vue'
import { WeekPicker } from '@/components/shared/datePickers'
import { formatDate } from '@/lib/display'
import { getLocation, getIPAddress } from '@/lib/miscellaneous'
import { MessageBox } from '@/components/shared/messages'
import { getCurrentUser } from '@/lib/authentication'
import { nextTick } from 'vue'
import NoDataAvailableMessage from '@/components/reports/NoDataAvailableMessage'
import { WithoutAssignmentEntriesView } from '@/components/consultantTimeSheet/'

export default {
  components: {
    SimplePageHeader,
    ClockDayLog,
    BottomSpacer,
    WeekPicker,
    MessageBox,
    NoDataAvailableMessage,
    WithoutAssignmentEntriesView
  },
  data () {
    return {
      dateFilter: null,
      dateBeginFilter: null,
      dateEndFilter: null,
      messageBoxText: null,
      messageBoxColor: null,
      geolocationPosition: null,
      selectedAssignmentId: null,
      clockIPAddress: null,
      assignmentsOptionsList: null,
      showNoDataMessage: true,
      weekTotalHours: 0
    }
  },
  computed: {
    ...mapFields('clockLogs', [
      'clockWeekLogs'
    ]),
    acceptLabel () {
      if (this.currentWeek) {
        let log = this.getLastRecordForToday()
        if (log && log.state === 0) {
          return 'Clock Out'
        }
        return 'Clock In'
      }
      return null
    },
    currentWeek () {
      return this.dateFilter >= new Date(this.dateBeginFilter) && this.dateFilter <= new Date(this.dateEndFilter)
    }
  },
  watch: {
    clockWeekLogs () {
      this.weekTotalHours = this.clockWeekLogs.clockDaysLog.reduce((acc, row) => {
        return acc + row.workedTime
      }, 0)
      this.showNoDataMessage = !(this.clockWeekLogs.clockDaysLog && this.clockWeekLogs.clockDaysLog.length > 0)
      let assignmentsFiltered = this.assignmentsOptionsList.filter(a => a.value === this.selectedAssignmentId)
      this.clockIPAddress = assignmentsFiltered && assignmentsFiltered.length > 0 ? assignmentsFiltered[0].clockIPAddress : null
    }
  },
  methods: {
    ...mapActions('clockLogs', [
      'getClockWeekLogs',
      'clock'
    ]),
    ...mapActions('assignments', [
      'getAssignmentsByUserId'
    ]),
    ...mapActions('datetimeHelper', [
      'getStaffLynxTime',
      'getUTCTime'
    ]),
    async getLocationSuccessHandler (position) {
      let latitude = position.coords.latitude
      let longitude = position.coords.longitude
      let ipAddress = await getIPAddress()
      let todayLastRecord = this.getLastRecordForToday()
      let state = todayLastRecord ? (todayLastRecord.state === 0 ? 1 : 0) : 0
      let newRecord = {
        assignmentId: this.selectedAssignmentId,
        utcTime: new Date(await this.getUTCTime()),
        latitude: latitude,
        longitude: longitude,
        iPAddress: ipAddress,
        state: state
      }
      if (this.clockIPAddress) {
        if (this.clockIPAddress !== ipAddress) {
          this.messageBoxText = 'Your IP address does not match the required IP address to ' + this.acceptLabel
          this.messageBoxColor = 'error'
          this.$refs.messageBox.show()
        } else {
          await this.clock(newRecord)
          if (this.selectedAssignmentId) {
            await this.getClockWeekLogs({ assignmentId: this.selectedAssignmentId, dateBegin: formatDate(this.dateFilter) })
          }
        }
      } else {
        await this.clock(newRecord)
        if (this.selectedAssignmentId) {
          await this.getClockWeekLogs({ assignmentId: this.selectedAssignmentId, dateBegin: formatDate(this.dateFilter) })
        }
      }
    },
    getLocationErrorHandler (error) {
      if (error.code === 1) {
        this.messageBoxText = 'Please allow your browser to get your location to ' + this.acceptLabel
      } else {
        this.messageBoxText = 'Unable to get your location, please try again or use another device to ' + this.acceptLabel
      }
      this.messageBoxColor = 'error'
      this.$refs.messageBox.show()
    },
    geolocationNotSupportedCallback () {
      this.messageBoxColor = 'error'
      this.messageBoxText = 'Your browser does not support geolocation, try again with another browser or use another device to ' + this.acceptLabel
      this.$refs.messageBox.show()
    },
    getLastRecordForToday () {
      if (this.currentWeek &&
        this.clockWeekLogs.clockDaysLog &&
        this.clockWeekLogs.clockDaysLog.length > 0
      ) {
        let todayLogs = this.clockWeekLogs.clockDaysLog.filter(
          (day) => new Date(day.staffLynxLocalDateTime).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)
        )[0]
        if (todayLogs && todayLogs.clockLogs && todayLogs.clockLogs.length > 0) {
          return todayLogs.clockLogs[todayLogs.clockLogs.length - 1]
        }
        return null
      }
      return null
    },
    weekChangeHandler (dateBegin, dateEnd) {
      this.dateBeginFilter = dateBegin
      this.dateEndFilter = dateEnd
      if (this.selectedAssignmentId) {
        this.getClockWeekLogs({ assignmentId: this.selectedAssignmentId, dateBegin: dateBegin })
      }
    },
    selectedAssignmentChangeHandler () {
      if (this.selectedAssignmentId) {
        this.getClockWeekLogs({ assignmentId: this.selectedAssignmentId, dateBegin: formatDate(this.dateBeginFilter) })
      }
    },
    async clockHandler () {
      if (this.currentWeek) {
        await getLocation(this.getLocationSuccessHandler, this.getLocationErrorHandler, this.geolocationNotSupportedCallback)
      }
    }
  },
  async created () {
    this.dateFilter = new Date(new Date(await this.getStaffLynxTime()).setHours(0, 0, 0, 0))
    let assignmentsList = await this.getAssignmentsByUserId(getCurrentUser().id)
    assignmentsList = assignmentsList.filter(a => a.timeSheetType === 3)
    this.showNoDataMessage = !(assignmentsList && assignmentsList.length > 0)
    if (!this.showNoDataMessage) {
      this.selectedAssignmentId = assignmentsList[0].id
      this.clockIPAddress = assignmentsList[0].clockIPAddress
      this.assignmentsOptionsList = assignmentsList.map(assignment => {
        return {
          value: assignment.id,
          text: assignment.clientName + ' - ' + assignment.jobTitle,
          clockIPAddress: assignment.clockIPAddress
        }
      })
      await nextTick()
      if (this.selectedAssignmentId) {
        this.getClockWeekLogs({ assignmentId: this.selectedAssignmentId, dateBegin: formatDate(this.dateFilter) })
      }
    }
  }
}
</script>
